import { Table, Button } from 'react-bootstrap';
import { useGetStocksQuery } from '../../slices/stocksApiSlice';
import { useGetSuppliesQuery } from '../../slices/suppliesApiSlice';
import { useGetTransfersQuery } from '../../slices/transfersApiSlice';
import { useGetExpensesQuery } from '../../slices/expensesApiSlice';
import { useGetIncomesQuery } from '../../slices/incomesApiSlice';
import Message from '../../components/Message';
import Loader from '../../components/Loader';
import { useState, useMemo, useCallback } from 'react';
import * as XLSX from 'xlsx';

const WorkingCapital = () => {
  const { data: stocksData, isLoading, error } = useGetStocksQuery();
  const { data: suppliesData } = useGetSuppliesQuery();
  const { data: transfersData } = useGetTransfersQuery();

  const stocks = useMemo(() => stocksData?.stocks || [], [stocksData]);
  const supplies = useMemo(() => suppliesData?.supplies || [], [suppliesData]);
  const transfers = useMemo(() => transfersData?.transfers || [], [transfersData]);
  
  const [stockValues, setStockValues] = useState({});

  // Extract unique dates
  const uniqueDates = useMemo(() => {
    return stocks.length > 0 
      ? [...new Set(stocks.map(stock => new Date(stock.date).toISOString().split('T')[0]))]
      : [];
  }, [stocks]);

  const sortedDates = useMemo(() => uniqueDates.sort((a, b) => new Date(a) - new Date(b)), [uniqueDates]);
  const date1 = sortedDates;
  const date2 = [...sortedDates.slice(1), sortedDates[sortedDates.length - 1]];

  const { data: expensesData } = useGetExpensesQuery({
    startDate: date1,
    endDate: date2,
  });
  const { data: incomesData} = useGetIncomesQuery({
    startDate: date1,
    endDate: date2,
  });

  const expenses = useMemo(() => expensesData?.expenses || [], [expensesData]);
  const incomes = useMemo(() => incomesData?.incomes || [], [incomesData]);

  

  // Function to calculate stock value using stockItems
  const calculateStockValue = useCallback((date) => {
    return stocks
      .filter(stock => new Date(stock.date).toISOString().split('T')[0] === date)
      .flatMap(stock => stock.stockItems || [])
      .reduce((acc, item) => acc + (Number(item.price) || 0) * (Number(item.qty) || 0), 0);
  }, [stocks]);

  // Function to calculate Supply Value
   const calculateSupplyValue = useCallback((startDate, endDate) => {
  return supplies
    .filter(supply => {
      const supplyDate = new Date(supply.date).toISOString().split('T')[0];
      return supplyDate >= startDate && supplyDate < endDate;
    })
    .flatMap(supply => supply.supplyItems || [])
    .reduce((acc, item) => acc + (Number(item.price) || 0) * (Number(item.qty) || 0), 0);
}, [supplies]);

// Function to calculate Transfer Value
  const calculateTransferValue = useCallback((startDate, endDate) => {
  return transfers
    .filter(transfer => {
      const transferDate = new Date(transfer.date).toISOString().split('T')[0];
      return transferDate >= startDate && transferDate < endDate;
    })
    .flatMap(transfer => transfer.transferItems || [])
    .reduce((acc, item) => acc + (Number(item.price) || 0) * (Number(item.qty) || 0), 0);
}, [transfers]);

  // Function to calculate Expense Value
  const calculateExpenseValue = useCallback((startDate, endDate) => {
  if (!startDate || !endDate) return 0;

  return expenses
    .filter(expense => {
      const expenseDate = new Date(expense.date).getTime();
      return expenseDate >= new Date(startDate).getTime() && expenseDate < new Date(endDate).getTime();
    })
    .reduce((acc, expense) => acc + Number(expense.amount || 0), 0);
}, [expenses]);
  
  // Function to calculate Income Value

  const calculateIncomeValue = useCallback((startDate, endDate) => {
  if (!startDate || !endDate) return 0;

  return incomes
    .filter(income => {
      const incomeDate = new Date(income.date).getTime();
      return incomeDate >= new Date(startDate).getTime() && incomeDate < new Date(endDate).getTime();
    })
    .reduce((acc, income) => acc + Number(income.amount || 0), 0);
}, [incomes]);

// Function to calculate only SalesCredit-related expenses within the date range
  const calculateSalesCreditExpenseValue = (startDate, endDate) => {
    return expensesData?.expenses
      ?.filter(expense => {
        const expenseDate = new Date(expense.date).toISOString().split('T')[0];
        return expense.category === "SalesCredit" && expenseDate >= startDate && expenseDate < endDate;
      })
      .reduce((sum, expense) => sum + Number(expense.amount || 0), 0) || 0;
  };

  const calculateSalesPaymentExpenseValue = (startDate, endDate) => {
  return expensesData?.expenses
    ?.filter(expense => {
      const expenseDate = new Date(expense.date).toISOString().split('T')[0];
      return expense.category === "SalesPayment" && expenseDate >= startDate && expenseDate < endDate;
    })
    .reduce((sum, expense) => sum + Number(expense.amount || 0), 0) || 0;
};


  // Function to manually refresh stock values
  const recalculateStockValues = () => {
    const updatedValues = date1.reduce((acc, date, index) => {
      const startDate = date;
      const endDate = date2[index];

      const openingStockValue = calculateStockValue(date);
      const supplyValue = calculateSupplyValue(startDate, endDate);
      const transferValue = calculateTransferValue(startDate, endDate);
      const expenseTotal = calculateExpenseValue(startDate, endDate);
      const incomeTotal = calculateIncomeValue(startDate, endDate);
      const expenseSalesCreditTotal = calculateSalesCreditExpenseValue(startDate, endDate);
      const closingStockValue = calculateStockValue(endDate);
      

      const netValue = openingStockValue + supplyValue + expenseTotal - transferValue - closingStockValue;
      const varValue = incomeTotal - netValue;
      const expenseSalesPaymentTotal = calculateSalesPaymentExpenseValue(startDate, endDate);
     const increaseInReceivables = expenseSalesCreditTotal + expenseSalesPaymentTotal;
     const reValue = increaseInReceivables < 0 
  ? varValue - increaseInReceivables 
  : varValue;

     acc[date] = {
  openingStockValue,
  supplyValue,
  transferValue,
  expenseValue: expenseTotal,
  expenseSalesCreditValue: expenseSalesCreditTotal,
  expenseSalesPaymentValue: expenseSalesPaymentTotal,
  increaseInReceivables,
  closingStockValue,
  netValue,
  incomeValue: incomeTotal,
  varValue,
  reValue,
   };

      
      return acc;
    }, {});

    setStockValues(updatedValues);
  };


  if (isLoading) return <Loader />;
  if (error) return <Message variant="danger">{error?.data?.message || 'Error fetching stock data'}</Message>;
  if (stocks.length === 0) return <Message variant="warning">No stock data available</Message>;
  
  const exportToExcel = () => {
  const tableData = date1.map((date, index) => {
    const values = stockValues[date] || {};
    return {
      Date1: date,
      Date2: date2[index],
      'Opening Stock Value': values.openingStockValue ?? calculateStockValue(date),
      'Supply Value': values.supplyValue ?? calculateSupplyValue(date, date2[index]),
      'Transfer Value': values.transferValue ?? calculateTransferValue(date, date2[index]),
      'Expense Value': values.expenseValue ?? calculateExpenseValue(date, date2[index]),
      'Closing Stock Value': values.closingStockValue ?? calculateStockValue(date2[index]),
      'Net Value': values.netValue,
      'Income Value': values.incomeValue ?? calculateIncomeValue(date, date2[index]),
      'Var': values.varValue,
      'Credit': values.expenseSalesCreditValue ?? calculateSalesCreditExpenseValue(date, date2[index]),
      'Payment': values.expenseSalesPaymentValue ?? calculateSalesPaymentExpenseValue(date, date2[index]),
      'Receivable Inc': values.increaseInReceivables,
      'RVar': values.reValue,
    };
  });

  const worksheet = XLSX.utils.json_to_sheet(tableData);
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Working Capital');
  XLSX.writeFile(workbook, 'WorkingCapital.xlsx');
};

  return (
    <>
      <h1>Working Capital</h1>
      <Button onClick={recalculateStockValues} variant="primary" className="mb-3">
        Calculate Stock Values
      </Button>
      <Button onClick={exportToExcel} variant="success" className="mb-3 ms-2">
      Export to Excel
     </Button>
      <Table striped bordered hover responsive>
        <thead>
          <tr>
            <th>Date1</th>
            <th>Date2</th>
            <th>Opening Stock Value</th>
            <th>Supply Value</th>
            <th>Transfer Value</th>
            <th>Expense Value</th>
            <th>Closing Stock Value</th>
            <th>Net Value</th>
            <th>Income Value</th>
            <th>Var</th>
            <th>Credit</th>
            <th>Payment</th>
            <th>Receivable Inc</th>
            <th style={{ display: 'none' }}>RVar</th>
          </tr>
        </thead>
        <tbody>
          {date1.map((date, index) => (
            <tr key={index}>
              <td>{date}</td>
              <td>{date2[index]}</td>
              <td>{stockValues[date]?.openingStockValue ?? calculateStockValue(date1[index])}</td>
              <td>{stockValues[date]?.supplyValue ?? calculateSupplyValue(date, date2[index])}</td>
              <td>{stockValues[date]?.transferValue ?? calculateTransferValue(date, date2[index])}</td>
              <td>{stockValues[date]?.expenseValue ?? calculateExpenseValue(date1, date2[index])}</td>
              <td>{stockValues[date]?.closingStockValue ?? calculateStockValue(date2[index])}</td>
              <td>{stockValues[date]?.netValue}</td>
              <td>{stockValues[date]?.incomeValue ?? calculateIncomeValue(date1, date2[index])}</td>
              <td>{stockValues[date]?.varValue}</td>
              <td>{stockValues[date]?.expenseSalesCreditValue ?? calculateSalesCreditExpenseValue(date1, date2[index])}</td>
  
              <td>{stockValues[date]?.expenseSalesPaymentValue ?? calculateSalesPaymentExpenseValue(date1[index], date2[index])}</td>
              <td>{stockValues[date]?.increaseInReceivables}</td>
              <td style={{ display: 'none' }}>{stockValues[date]?.reValue}</td>
              
              
            </tr>
          ))}
        </tbody>
      </Table>
    </>
  );
};

export default WorkingCapital;